import type { StatControllerListResponse } from 'api/stat/Stat.schema.ts';
import { dateToLocalYMDHM } from 'common/date.ts';
import dayjs from 'common/dayjs.ts';
import { getRuntimeTimezone, isGranted, POST, toQuerystring } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { IBellRinging, ICheckbox, IClock } from 'ui/component/Icons.tsx';
import { Input } from 'ui/control/Input.tsx';
import { Select } from 'ui/control/Select.tsx';
import { KPI } from '#admin/component/KPI.tsx';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

const INTERVAL_OPTIONS = ['1D', '2D', '3D', '7D', '14D', '30D', '45D'] as const;
type INTERVAL_OPTION = (typeof INTERVAL_OPTIONS)[number];
type FormValues = {
  tz: string;
  interval: INTERVAL_OPTION;
};

const querystringParserOptions = { arrayKeys: ['status$in'] };

export const Home = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const env = useEnv();

  const formContext = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      tz: getRuntimeTimezone(),
      interval: '7D',
    },
  });
  const { handleSubmit, watch } = formContext;

  const interval = watch('interval', '7D');

  const [delivery_datetime$from, delivery_datetime$to] = React.useMemo(() => {
    const [amount, days] = interval.match(/\d+|\D+/g) as [any, any];
    if (!amount || !days) throw new Error('Invalid interval');
    const delivery_datetime$from = dayjs().subtract(amount, 'day').startOf('day').toISOString();
    const delivery_datetime$to = dayjs().endOf('day').toISOString();
    return [delivery_datetime$from, delivery_datetime$to];
  }, [interval]);

  const collection_datetime$from = dayjs().add(1, 'day').startOf('day').toISOString();

  const { data: stats, error } = useSWR<StatControllerListResponse>(['/stat/list', { collection_datetime$from, delivery_datetime$to, delivery_datetime$from }], POST);

  const onSubmit = () => {};

  if (error) return <div>{t('messages.errorFetchingKPIs', { message: error.message })}</div>;

  return (
    <main className="container m-auto flex w-full flex-col">
      <div className="mt-8 flex flex-col gap-12 p-4 px-16 pb-16">
        <section>
          <h1 className="mb-4 font-semibold text-3xl">{t('kpi.sections.scheduledShipments')}</h1>
          <div className="kpi-row">
            <KPI
              title={t('kpi.scheduledShipments.title')}
              description={t('kpi.scheduledShipments.description')}
              value={stats?.SCHEDULED_SHIPMENTS}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['pending'] }, querystringParserOptions)}
            />

            <KPI
              title={t('kpi.scheduledDelayedShipments.title')}
              description={t('kpi.scheduledDelayedShipments.description')}
              value={stats?.SCHEDULED_DELAYED_SHIPMENTS}
              percentage={stats?.SCHEDULED_DELAYED_PERCENT}
              // upperThresholdPercentage={5}
              sampleSize={stats?.SCHEDULED_SHIPMENTS}
              color={'red'}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['pending'], delayed: true }, querystringParserOptions)}
            />
          </div>
        </section>

        <section>
          <h1 className="mb-4 font-semibold text-3xl">{t('kpi.sections.activeShipments')}</h1>
          <div className="kpi-row">
            <KPI
              title={t('kpi.activeShipments.title')}
              description={t('kpi.activeShipments.description')}
              value={stats?.ACTIVE_SHIPMENTS}
              sampleSize={stats?.ACTIVE_SHIPMENTS}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['transiting'] }, querystringParserOptions)}
            />
            <KPI
              title={t('kpi.activeDelayedShipments.title')}
              description={t('kpi.activeDelayedShipments.description')}
              icon={<IClock />}
              value={stats?.ACTIVE_DELAYED_SHIPMENTS}
              percentage={stats?.ACTIVE_DELAYED_PERCENT}
              // upperThresholdPercentage={5}
              sampleSize={stats?.ACTIVE_SHIPMENTS}
              color={'red'}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['transiting'], delayed: true }, querystringParserOptions)}
            />
            <KPI
              title={t('kpi.activeOnTime.title')}
              description={t('kpi.activeOnTime.description')}
              icon={<ICheckbox />}
              value={stats?.ACTIVE_ON_TIME_SHIPMENTS}
              percentage={stats?.ACTIVE_ON_TIME_PERCENT}
              // lowerThresholdPercentage={95}
              sampleSize={stats?.ACTIVE_SHIPMENTS}
              color={'green'}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['transiting'], delayed: true }, querystringParserOptions)}
            />
            {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
              <KPI
                title={t('kpi.activeAlertedShipments.title')}
                description={t('kpi.activeAlertedShipments.description')}
                icon={<IBellRinging />}
                value={stats?.ACTIVE_ALERTED_SHIPMENTS}
                percentage={stats?.ACTIVE_ALERTED_PERCENT}
                // upperThreshold={1}
                color={'red'}
                sampleSize={stats?.ACTIVE_SHIPMENTS}
                targetURL={'/shipments?' + toQuerystring({ status$in: ['transiting'], unarchived_alerts: true }, querystringParserOptions)}
              />
            )}
          </div>
        </section>

        <section>
          <div className="mb-4 flex flex-col items-start justify-start gap-2">
            <h1 className="font-semibold text-3xl">{t('kpi.sections.deliveredShipments')}</h1>

            <FormProvider {...formContext}>
              <form className="flex flex-row items-center gap-2" id="interval-form" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Select
                  rootClassName="flex-row gap-3 items-center"
                  labelClassName="!m-0"
                  className="!h-10 w-[240px] bg-white"
                  name={'interval'}
                  label={t('labels.interval')}
                  required={true}
                  placeholder={t('actions.choose')}
                  options={INTERVAL_OPTIONS as unknown as string[]}
                  registerOptions={{ required: true }}
                  autoSubmitForm={'#interval-form'}
                />

                {/* we add as the last one so it doesn't apply gap */}
                <Input name="tz" type="hidden" />

                <span className="flex flex-row gap-2">
                  <span>{t('kpi.intervalRange.from')}</span>
                  <strong>{dateToLocalYMDHM(delivery_datetime$from)}</strong>
                  <span>{t('kpi.intervalRange.to')}</span>
                  <strong>{dateToLocalYMDHM(delivery_datetime$to)}</strong>
                </span>
              </form>
            </FormProvider>
          </div>

          <div className="kpi-row">
            <KPI
              title={t('kpi.deliveredShipments.title')}
              description={t('kpi.deliveredShipments.description')}
              value={stats?.DELIVERED_SHIPMENTS}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['delivered'], delivery_datetime$from, delivery_datetime$to }, querystringParserOptions)}
            />
            <KPI
              title={t('kpi.deliveredDelayed.title')}
              description={t('kpi.deliveredDelayed.description')}
              icon={<IClock />}
              value={stats?.DELIVERED_DELAYED_SHIPMENTS}
              percentage={stats?.DELIVERED_DELAYED_PERCENT}
              // upperThresholdPercentage={5}
              sampleSize={stats?.DELIVERED_SHIPMENTS}
              color={'red'}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['delivered'], delayed: true, delivery_datetime$from, delivery_datetime$to }, querystringParserOptions)}
            />
            <KPI
              title={t('kpi.deliveredOnTime.title')}
              description={t('kpi.deliveredOnTime.description')}
              icon={<ICheckbox />}
              value={stats?.DELIVERED_ON_TIME_SHIPMENTS}
              percentage={stats?.DELIVERED_ON_TIME_PERCENT}
              // lowerThresholdPercentage={95}
              sampleSize={stats?.DELIVERED_SHIPMENTS}
              color={'green'}
              targetURL={'/shipments?' + toQuerystring({ status$in: ['delivered'], delayed: false, delivery_datetime$from, delivery_datetime$to }, querystringParserOptions)}
            />
            {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
              <KPI
                title={t('kpi.deliveredOnSpec.title')}
                description={t('kpi.deliveredOnSpec.description')}
                value={stats?.DELIVERED_ON_SPEC_SHIPMENTS}
                percentage={stats?.DELIVERED_ON_SPEC_PERCENT}
                // lowerThresholdPercentage={95}
                sampleSize={stats?.DELIVERED_SHIPMENTS}
                color={'green'}
                targetURL={
                  '/shipments?' +
                  toQuerystring({ status$in: ['delivered'], unarchived_alerts: false, delayed: false, delivery_datetime$from, delivery_datetime$to }, querystringParserOptions)
                }
              />
            )}
          </div>
        </section>
      </div>
    </main>
  );
};
